/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { WorkbenchContext } from './types';
import { resolveJinjaFilterKind } from '../jinja/generateJinjaFilterClause';

export interface SqlLabFilterParam {
  col: string;
  op: string;
  val: unknown;
}

function isTemporalFilter(
  filter: WorkbenchContext['nativeFilters'][number],
): boolean {
  return (
    resolveJinjaFilterKind(filter.filterType, filter.valueType) === 'temporal'
  );
}

function isRangeFilter(
  filter: WorkbenchContext['nativeFilters'][number],
): boolean {
  return (
    resolveJinjaFilterKind(filter.filterType, filter.valueType) === 'range'
  );
}

/** Dashboard ctx → SQL Lab template params for Jinja trial runs. */
export function buildTemplateParamsFromContext(
  ctx: WorkbenchContext,
): Record<string, unknown> {
  const legacyFilters: Record<string, unknown> = {};
  const filters: SqlLabFilterParam[] = [];
  let from_dttm: string | undefined;
  let to_dttm: string | undefined;

  ctx.nativeFilters.forEach(filter => {
    if (isTemporalFilter(filter)) {
      const [start, end] = filter.values ?? [];
      if (start != null) {
        from_dttm = String(start);
      }
      if (end != null) {
        to_dttm = String(end);
      }
      return;
    }

    if (isRangeFilter(filter)) {
      const [min, max] = filter.values ?? [];
      if (min != null) {
        filters.push({ col: filter.column, op: '>=', val: min });
      }
      if (max != null) {
        filters.push({ col: filter.column, op: '<=', val: max });
      }
      legacyFilters[filter.column] = filter.values;
      return;
    }

    if (filter.values?.length) {
      legacyFilters[filter.column] = filter.values;
      filters.push({ col: filter.column, op: 'IN', val: filter.values });
    }
  });

  return {
    _filters: legacyFilters,
    filters,
    from_dttm,
    to_dttm,
    dashboard_id: ctx.dashboardId,
    chart_id: ctx.chartId,
  };
}
